'use client'

import { SbBlokData, storyblokEditable } from '@storyblok/react/rsc'
import { FC, useEffect, useState } from 'react'

import { RegionProps } from '@/components/02-molecules/PartnerList/PartnerList'
import { transformPartnerDataToMapData } from '@/lib/mapDataTransforms'

import { InteractiveMap } from './InteractiveMap'

interface InteractiveMapBlokProps {
  blok: {
    title?: string
    regions?: SbBlokData[]
    showMap?: boolean
    className?: string
    padding?: SbBlokData[]
    background?: SbBlokData[]
    colour?: SbBlokData[]
    alignment?: SbBlokData[]
    groupStylePanel?: SbBlokData[]
    group_style_panel?: SbBlokData[]
  } & SbBlokData
  rels?: any[]
}

// Clean office name by removing "Findex" prefix and "-old"/"- old" suffix
const cleanOfficeName = (name: string): string => {
  return name
    .replace(/^Findex\s+/i, '') // Remove "Findex " prefix
    .replace(/\s*-\s*old$/i, '') // Remove "- old" or "-old" suffix
    .trim()
}

const InteractiveMapBlok: FC<InteractiveMapBlokProps> = ({ blok }) => {
  const [stateData, setStateData] = useState({})

  useEffect(() => {
    if (blok.regions && blok.regions.length > 0) {
      // Transform Storyblok regions data (assuming it's already resolved like in PartnerList)
      const regionsData: RegionProps[] = blok.regions
        .map((regionData: any) => {
          // Get partners for this region
          const partners = regionData.partners || []

          // Group partners by their main office (first office [0])
          const officeGroups: Record<string, { name: string; advisers: { name: string }[] }> = {}

          partners.forEach((partner: any) => {
            const partnerName = partner.name || 'Unknown Partner'

            // Get partner's offices
            const partnerOffices = partner.content?.offices || []
            if (partnerOffices.length === 0) return

            // Use first office as main office
            const mainOffice = partnerOffices[0]
            const rawOfficeName = mainOffice.content?.title || mainOffice.name || 'Unknown Office'
            const cleanedOfficeName = cleanOfficeName(rawOfficeName)

            if (!officeGroups[cleanedOfficeName]) {
              officeGroups[cleanedOfficeName] = { name: cleanedOfficeName, advisers: [] }
            }

            // Check if partner already exists in this office (prevent duplicates)
            const partnerExists = officeGroups[cleanedOfficeName].advisers.some(
              (existingAdviser) => existingAdviser.name === partnerName
            )

            if (!partnerExists) {
              officeGroups[cleanedOfficeName].advisers.push({ name: partnerName })
            }
          })

          const offices = Object.values(officeGroups)

          return {
            name: regionData.name || 'Unknown Region',
            partners: partners, // Keep original partners structure for compatibility
            offices, // Add the processed offices
          }
        })
        .filter(Boolean) as RegionProps[]

      // Transform the regions data into map state data
      const mapData = transformPartnerDataToMapData(regionsData)

      setStateData(mapData)
    } else {
      setStateData({})
    }
  }, [blok.regions])

  return (
    <div
      {...storyblokEditable(blok)}
      style={{ overflow: 'visible', height: '100%', width: '100%' }}
    >
      <InteractiveMap stateData={stateData} className={blok.className} />
    </div>
  )
}

export default InteractiveMapBlok
